import { useCollection } from '@amzn/awsui-collection-hooks';
import { AppLayout, Box, BoxProps, Button, Header, PropertyFilter, PropertyFilterProps, SpaceBetween, Table } from '@amzn/awsui-components-core';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApplicationFlatTableEntity } from 'src/components/context/AppContextModels';
import { useAppContext } from 'src/components/context/AppContextProvider';
import { getFilteringProperties, getUniqueApplicationFilteringOptionsForAllApps } from 'src/components/context/AppContextUtils';
import { useAuth } from 'src/components/context/AuthContextProvider';
import { NavigationLink } from 'src/components/generic-components/NavigationLink';
import {
  BLANK_SEARCH_AND,
  Preferences,
  TableEmptyState,
  TableNoMatchState,
  getMatchesCountText
} from 'src/components/generic-components/TableCommons';
import { HomePageSideNavigationV2 } from 'src/components/side-navigation-v2/HomePageSideNavigationV2';
import { eUserPreferenceKeys } from 'src/constants/AppConstants';
import { usePersistedState } from 'src/hooks/useLocalStorage';
import { appLayoutAriaLabels, propertyFilterI18nStrings } from 'src/i18n-strings';
import { Titles } from '../das-finsuite-constants';
import { APPLICATIONS_DEFAULT_PREFERENCES, APPLICATION_COLUMN_DEFINITIONS, DEFAULT_VISIBLE_CONTENT_OPTIONS } from './AllAppsConfig';

const AllApps: React.FC = () => {
  const appContext = useAppContext();
  const { isAdmin } = useAuth();
  const activeParsedApplications = appContext.parsedApplications.filter((parsedApps) => parsedApps.isActive);
  const navigate = useNavigate();

  const [filteringProperties, setFilteringProperties] = useState<PropertyFilterProps.FilteringProperty[]>(getFilteringProperties(false));
  const [filteringOptions, setFilteringOptions] = useState<PropertyFilterProps.FilteringOption[]>([]);
  const [preferences, setPreferences] = usePersistedState(eUserPreferenceKeys.ALL_APPS_TABLE_PREFERENCES, APPLICATIONS_DEFAULT_PREFERENCES);
  const [query, setQuery] = useState(BLANK_SEARCH_AND);

  useEffect(() => {
    applicationCollection.actions.setPropertyFiltering(query);
  }, [query]);

  useEffect(() => {
    if (activeParsedApplications.length > 0) {
      setFilteringOptions(getUniqueApplicationFilteringOptionsForAllApps(activeParsedApplications));
    }
  }, [appContext.parsedApplications]);

  const applicationCollection = useCollection(activeParsedApplications, {
    propertyFiltering: {
      filteringProperties,
      empty: <TableEmptyState resourceName="Applications" />,
      noMatch: (
        <TableNoMatchState
          onClearFilter={() => {
            applicationCollection.actions.setPropertyFiltering({ tokens: [], operation: 'and' });
          }}
        />
      )
    },
    sorting: {
      defaultState: {
        sortingColumn: {
          sortingField: 'applicationOwner',
          sortingComparator: (a: ApplicationFlatTableEntity, b: ApplicationFlatTableEntity) => a.applicationOwner.localeCompare(b.applicationOwner)
        },
        isDescending: false
      }
    }
  });

  return (
    <AppLayout
      ariaLabels={appLayoutAriaLabels}
      contentType="table"
      navigation={<HomePageSideNavigationV2 />}
      toolsHide={true}
      stickyNotifications
      content={
        <Table
          {...applicationCollection.collectionProps}
          variant="full-page"
          stickyHeader={true}
          enableKeyboardNavigation={true}
          items={applicationCollection.items}
          columnDefinitions={isAdmin ? APPLICATION_COLUMN_DEFINITIONS : APPLICATION_COLUMN_DEFINITIONS.filter((c) => c.id !== 'edit')}
          visibleColumns={preferences.visibleContent}
          resizableColumns={false}
          wrapLines={preferences.wrapLines}
          stripedRows={preferences.stripedRows}
          contentDensity={preferences.contentDensity}
          stickyColumns={isAdmin ? { last: 1 } : undefined}
          header={
            <Header
              actions={
                <>
                  {appContext.isAdminControlsEnabled && (
                    <SpaceBetween size="m" direction="horizontal">
                      <Button variant="normal" onClick={() => navigate('/admin/applications')}>
                        {'Manage in Admin Console'}
                      </Button>
                      <Button iconName="add-plus" variant="primary" onClick={() => navigate('/admin/manage-applications')}>
                        {'New Application'}
                      </Button>
                    </SpaceBetween>
                  )}
                </>
              }
              variant="h1"
            >
              {Titles.TITLE_ALL_APPS}
            </Header>
          }
          filter={
            <PropertyFilter
              i18nStrings={propertyFilterI18nStrings('Applications')}
              countText={getMatchesCountText(applicationCollection.filteredItemsCount!)}
              expandToViewport={true}
              {...applicationCollection.propertyFilterProps}
              query={query}
              onChange={(event) => {
                setQuery(event.detail.tokens?.length === 0 ? BLANK_SEARCH_AND : event.detail);
              }}
              filteringOptions={filteringOptions}
              filteringProperties={filteringProperties}
            />
          }
          preferences={
            <Preferences
              preferences={preferences}
              setPreferences={setPreferences}
              visibleContentOptions={DEFAULT_VISIBLE_CONTENT_OPTIONS}
              disablePageSizeOptions={true}
              disableResizableColumnsOptions={true}
            />
          }
        />
      }
    />
  );
};

export default AllApps;

interface AppNameWithLinkProps {
  appName: string;
  appId: string;
  embeddingEnabled?: boolean;
  applicationLink?: string;
  fontSize?: BoxProps.FontSize;
  padding?: BoxProps.Spacing;
}
export const AppNameWithLink = ({ appName, appId, embeddingEnabled, applicationLink, fontSize, padding }: AppNameWithLinkProps) => {
  const navigate = useNavigate();

  if (embeddingEnabled) {
    const url = `/all-apps/${appId}`;
    return (
      <Box variant="awsui-key-label" fontWeight="normal" color="text-status-info" fontSize={fontSize || 'body-m'} padding={padding}>
        <NavigationLink href={url} onClick={() => navigate(url)}>
          {appName}
        </NavigationLink>
      </Box>
    );
  }

  if (applicationLink) {
    return (
      <Box variant="awsui-key-label" fontWeight="normal" color="text-status-info" fontSize={fontSize || 'body-m'} padding={padding}>
        <NavigationLink href={applicationLink} onClick={() => window.open(applicationLink, '_blank', 'noopener,noreferrer')}>
          {appName}
        </NavigationLink>
      </Box>
    );
  }

  return (
    <Box variant="awsui-key-label" fontWeight="normal" fontSize={fontSize || 'body-m'} padding={padding}>
      {appName}
    </Box>
  );
};
